'use client'

import { RegisterCardFormValues, useRegisterCardFormState } from '@features/payment-methods'
import { useTranslation } from '@shared/config/i18n'
import { useRef } from 'react'

import { HStack, Input, Typography, VStack } from '@podo-app/design-system-temp'

const RegisterCreditCardForm = () => {
  const t = useTranslation()
  const cardNumberInputRefs = useRef<Partial<{ [key in keyof RegisterCardFormValues]: HTMLInputElement }>>({})

  const { formState, validationNumeric, handleChange } = useRegisterCardFormState()

  const isCardNumberMaxLength = (value: string) => {
    return value.length === 4
  }

  const isCardExpiryDateMaxLength = (value: string) => {
    return value.length === 2
  }

  const isCardPasswordMaxLength = (value: string) => {
    return value.length === 2
  }

  const setElementRef = (name: keyof RegisterCardFormValues, element: Nullable<HTMLInputElement>) => {
    if (!element) {
      return
    }

    cardNumberInputRefs.current[name] = element
  }

  return (
    <VStack as="form" className="gap-4">
      <VStack className="gap-2">
        <Typography size="h6" className="text-gray-400">
          {t('paymentMethods.creditCard.cardNumber')}
        </Typography>
        <HStack className="gap-2">
          <Input
            ref={(element) => setElementRef('cardNumber1', element)}
            name="cardNumber1"
            className="w-full"
            type="text"
            maxLength={4}
            pattern="\d*"
            inputMode="numeric"
            value={formState.values.cardNumber1}
            onChange={(event) => {
              validationNumeric(event)
              handleChange(event)
              if (isCardNumberMaxLength(event.target.value)) {
                cardNumberInputRefs.current.cardNumber2?.focus()
              }
            }}
          />
          <Input
            ref={(element) => setElementRef('cardNumber2', element)}
            name="cardNumber2"
            className="w-full"
            type="password"
            maxLength={4}
            pattern="\d*"
            inputMode="numeric"
            value={formState.values.cardNumber2}
            onChange={(event) => {
              validationNumeric(event)
              handleChange(event)
              if (isCardNumberMaxLength(event.target.value)) {
                cardNumberInputRefs.current.cardNumber3?.focus()
              }
            }}
          />
          <Input
            ref={(element) => setElementRef('cardNumber3', element)}
            name="cardNumber3"
            className="w-full"
            type="password"
            maxLength={4}
            pattern="\d*"
            inputMode="numeric"
            value={formState.values.cardNumber3}
            onChange={(event) => {
              validationNumeric(event)
              handleChange(event)
              if (isCardNumberMaxLength(event.target.value)) {
                cardNumberInputRefs.current.cardNumber4?.focus()
              }
            }}
          />
          <Input
            ref={(element) => setElementRef('cardNumber4', element)}
            name="cardNumber4"
            className="w-full"
            type="text"
            maxLength={4}
            pattern="\d*"
            inputMode="numeric"
            value={formState.values.cardNumber4}
            onChange={(event) => {
              validationNumeric(event)
              handleChange(event)
              if (isCardNumberMaxLength(event.target.value)) {
                cardNumberInputRefs.current.cardExpiryMonth?.focus()
              }
            }}
          />
        </HStack>
      </VStack>

      <VStack className="gap-2">
        <Typography size="h6" className="text-gray-400">
          {t('paymentMethods.creditCard.expiry')}
        </Typography>
        <HStack className="gap-2">
          <Input
            ref={(element) => setElementRef('cardExpiryMonth', element)}
            name="cardExpiryMonth"
            placeholder="MM"
            className="w-full"
            type="text"
            maxLength={2}
            pattern="\d*"
            inputMode="numeric"
            value={formState.values.cardExpiryMonth}
            onChange={(event) => {
              validationNumeric(event)
              handleChange(event)
              if (isCardExpiryDateMaxLength(event.target.value)) {
                cardNumberInputRefs.current.cardExpiryYear?.focus()
              }
            }}
          />
          <Input
            ref={(element) => setElementRef('cardExpiryYear', element)}
            name="cardExpiryYear"
            placeholder="YY"
            className="w-full"
            type="text"
            maxLength={2}
            pattern="\d*"
            inputMode="numeric"
            value={formState.values.cardExpiryYear}
            onChange={(event) => {
              validationNumeric(event)
              handleChange(event)
              if (isCardExpiryDateMaxLength(event.target.value)) {
                cardNumberInputRefs.current.cardPassword?.focus()
              }
            }}
          />
        </HStack>
      </VStack>

      <VStack className="gap-2">
        <Typography size="h6" className="text-gray-400">
          {t('paymentMethods.creditCard.password')}
        </Typography>
        <Input
          ref={(element) => setElementRef('cardPassword', element)}
          name="cardPassword"
          placeholder={t('paymentMethods.creditCard.passwordPlaceholder')}
          className="w-full"
          type="password"
          maxLength={2}
          pattern="\d*"
          inputMode="numeric"
          value={formState.values.cardPassword}
          onChange={(event) => {
            validationNumeric(event)
            handleChange(event)
            if (isCardPasswordMaxLength(event.target.value)) {
              cardNumberInputRefs.current.birthDate?.focus()
            }
          }}
        />
      </VStack>

      <VStack className="gap-2">
        <Typography size="h6" className="text-gray-400">
          {t('paymentMethods.creditCard.birthDate')}
        </Typography>
        <Input
          ref={(element) => setElementRef('birthDate', element)}
          name="birthDate"
          placeholder={t('paymentMethods.creditCard.birthDatePlaceholder')}
          className="w-full"
          type="text"
          maxLength={10}
          pattern="\d*"
          inputMode="numeric"
          value={formState.values.birthDate}
          onChange={(event) => {
            validationNumeric(event)
            handleChange(event)
          }}
        />
        <ol className="gap-0.5 list-disc list-outside pl-5 marker:text-gray-400">
          <li>
            <Typography size="h6" className="text-gray-400 font-normal">
              {t('paymentMethods.creditCard.securityNotice')}
            </Typography>
          </li>
        </ol>
      </VStack>
    </VStack>
  )
}

export default RegisterCreditCardForm
